import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { BillData } from '../types/billing';

// Create styles for single-page layout
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 15,
    fontSize: 8,
    lineHeight: 1.2,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 2,
  },
  companySubtitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 7,
    color: '#000000',
    lineHeight: 1.1,
  },
  invoiceInfo: {
    alignItems: 'flex-end',
  },
  invoiceTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 4,
  },
  invoiceDetails: {
    fontSize: 7,
    color: '#000000',
    textAlign: 'right',
  },
  clientSection: {
    marginBottom: 8,
    padding: 6,
    backgroundColor: '#F8F9FA',
    borderRadius: 3,
  },
  sectionTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 3,
  },
  clientDetails: {
    fontSize: 7,
    color: '#000000',
    lineHeight: 1.3,
  },
  table: {
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#2C4A6B',
    padding: 4,
  },
  tableHeaderText: {
    color: '#FFFFFF',
    fontSize: 7,
    fontWeight: 'bold',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: '#E5E7EB',
    padding: 3,
  },
  tableCell: {
    fontSize: 7,
    color: '#000000',
  },
  srNo: { width: '5%', textAlign: 'center' },
  description: { width: '45%' },
  sac: { width: '10%', textAlign: 'center' },
  qty: { width: '10%', textAlign: 'center' },
  rate: { width: '15%', textAlign: 'right' },
  amount: { width: '15%', textAlign: 'right' },
  totalsSection: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 8,
  },
  totalsBox: {
    width: 200,
    padding: 6,
    borderWidth: 1,
    borderColor: '#000000',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 2,
  },
  totalLabel: {
    fontSize: 7,
    color: '#000000',
  },
  totalValue: {
    fontSize: 7,
    color: '#000000',
    fontWeight: 'bold',
  },
  totalRowFinal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
    borderTopWidth: 1,
    borderTopColor: '#000000',
    marginTop: 2,
  },
  paymentTerms: {
    marginBottom: 8,
    padding: 6,
    backgroundColor: '#F8F9FA',
    borderRadius: 3,
  },
  paymentTermsTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 3,
  },
  paymentTermsText: {
    fontSize: 7,
    color: '#000000',
    lineHeight: 1.3,
  },
  signatureSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: 8,
  },
  thankYou: {
    fontSize: 7,
    color: '#000000',
  },
  signatureBox: {
    alignItems: 'center',
  },
  signatureLabel: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 4,
  },
  signatureLine: {
    borderTopWidth: 0.5,
    borderTopColor: '#000000',
    borderStyle: 'dashed',
    paddingTop: 8,
    width: 120,
  },
  signatureText: {
    fontSize: 7,
    color: '#000000',
    textAlign: 'center',
  },
  bankDetails: {
    marginBottom: 8,
    padding: 6,
    backgroundColor: '#F8F9FA',
    borderRadius: 3,
  },
  bankTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 3,
  },
  bankInfo: {
    fontSize: 7,
    color: '#000000',
    lineHeight: 1.3,
  },
  footer: {
    marginTop: 8,
    paddingTop: 6,
    borderTopWidth: 0.5,
    borderTopColor: '#000000',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 7,
    color: '#000000',
    textAlign: 'center',
    lineHeight: 1.2,
  },
});

interface TaxInvoicePDFProps {
  invoice: BillData;
}

const TaxInvoicePDF: React.FC<TaxInvoicePDFProps> = ({ invoice }) => {
  const formatNumber = (num: number): string => {
    return (Math.round(num * 100) / 100).toFixed(2);
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString.trim() === '' || dateString.toUpperCase() === 'N/A') {
      return 'N/A';
    }
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch {
      return dateString; // Return as-is if not a valid date
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            {/* Logo placeholder - replace with actual logo if available */}
            <Text style={styles.companyName}>{invoice.companyName}</Text>
            <Text style={styles.companySubtitle}>{invoice.companySubtitle}</Text>
            <View style={styles.companyDetails}>
              <Text>{invoice.companyAddress}</Text>
              <Text>Ph: {invoice.companyPhone} | Email: {invoice.companyEmail}</Text>
              <Text>GSTIN: {invoice.companyGSTN} | PAN: {invoice.companyPAN}</Text>
            </View>
          </View>
          <View style={styles.invoiceInfo}>
            <Text style={styles.invoiceTitle}>TAX INVOICE</Text>
            <View style={styles.invoiceDetails}>
              <Text>Invoice No: {invoice.invoiceNumber}</Text>
              <Text>Invoice Date: {formatDate(invoice.invoiceDate)}</Text>
              <Text>Work Order No: {invoice.workOrderNumber}</Text>
              <Text>Work Order Date: {formatDate(invoice.workOrderDate)}</Text>
              {invoice.periodOfWork && <Text>Period Of Work: {invoice.periodOfWork}</Text>}
            </View>
          </View>
        </View>

        {/* Client Information */}
        <View style={styles.clientSection}>
          <Text style={styles.sectionTitle}>To,</Text>
          <View style={styles.clientDetails}>
            <Text style={{ fontWeight: 'bold' }}>{invoice.clientName}</Text>
            <Text>{invoice.clientAddress}</Text>
            <Text>GSTN: {invoice.clientGSTN}</Text>
            <Text>State: {invoice.clientState}, State Code: {invoice.clientStateCode}</Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.srNo]}>Sr. No.</Text>
            <Text style={[styles.tableHeaderText, styles.description]}>Description of Goods/Services</Text>
            <Text style={[styles.tableHeaderText, styles.sac]}>SAC</Text>
            <Text style={[styles.tableHeaderText, styles.qty]}>Qty</Text>
            <Text style={[styles.tableHeaderText, styles.rate]}>Rate</Text>
            <Text style={[styles.tableHeaderText, styles.amount]}>Amount</Text>
          </View>

          {invoice.items.map((item, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={[styles.tableCell, styles.srNo]}>{index + 1}</Text>
              <Text style={[styles.tableCell, styles.description]}>{item.description}</Text>
              <Text style={[styles.tableCell, styles.sac]}>{item.sac}</Text>
              <Text style={[styles.tableCell, styles.qty]}>{item.qty}</Text>
              <Text style={[styles.tableCell, styles.rate]}>{formatCurrency(item.rate)}</Text>
              <Text style={[styles.tableCell, styles.amount]}>{formatCurrency(item.qty * item.rate)}</Text>
            </View>
          ))}
        </View>

        {/* Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalsBox}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Sub Total:</Text>
              <Text style={styles.totalValue}>{formatCurrency(invoice.subTotal)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>CGST ({(invoice.gstRate / 2).toFixed(1)}%):</Text>
              <Text style={styles.totalValue}>{formatCurrency(invoice.cgstAmount)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>SGST ({(invoice.gstRate / 2).toFixed(1)}%):</Text>
              <Text style={styles.totalValue}>{formatCurrency(invoice.sgstAmount)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Total GST ({invoice.gstRate}%):</Text>
              <Text style={styles.totalValue}>{formatCurrency(invoice.totalGstAmount)}</Text>
            </View>
            <View style={styles.totalRowFinal}>
              <Text style={[styles.totalLabel, { fontWeight: 'bold' }]}>Total:</Text>
              <Text style={[styles.totalValue, { fontWeight: 'bold' }]}>{formatCurrency(invoice.totalAmount)}</Text>
            </View>
          </View>
        </View>

        {/* Bank Details */}
        <View style={styles.bankDetails}>
          <Text style={styles.bankTitle}>OUR BANK DETAILS FOR PAYMENT:</Text>
          <View style={styles.bankInfo}>
            <Text>BANK NAME: {invoice.bankDetails.bankName}</Text>
            <Text>BRANCH: {invoice.bankDetails.branch}</Text>
            <Text>A/c No: {invoice.bankDetails.accountNumber}</Text>
            <Text>IFSC CODE: {invoice.bankDetails.ifscCode}</Text>
          </View>
        </View>

        {/* Terms and Conditions */}
        <View style={styles.paymentTerms}>
          <Text style={styles.paymentTermsTitle}>Terms and Conditions</Text>
          <View style={styles.paymentTermsText}>
            {invoice.termsAndConditions.map((term, index) => (
              <Text key={index} style={{ marginBottom: 2 }}>
                • {term}
              </Text>
            ))}
          </View>
        </View>

        {/* Signature Section */}
        <View style={styles.signatureSection}>
          <View style={styles.thankYou}>
            <Text>Thank you for your business!</Text>
            <Text>For queries, contact: {invoice.companyPhone}</Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Authorized Signature</Text>
            <View style={styles.signatureLine}>
              <Text style={styles.signatureText}>Authorized Signature</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            This invoice is valid for 30 days from the date of issue.
            {'\n'}
            For any discrepancies, please notify us within 7 days.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default TaxInvoicePDF;